import ApiHelper from '../../helpers/ApiHelper'

export const toApiDate = (date) => {
    if (date instanceof Date) {
        return date.toISOString().split('T')[0]
    }

    const value = `${date || ''}`.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value
    }

    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0]
    }

    return value
}

export class ApiService {

    getBaseURL() {
        return ApiHelper.getBaseURL()
    }

    async login(login) {
        return await ApiHelper.post(`/api/usuarios/login`, login)
    }

    async seleccionarEmpresa(data) {
        return await ApiHelper.post(`/api/usuarios/seleccionar-empresa`, data)
    }

    async cambiarEmpresa(data) {
        return await ApiHelper.post(`/api/usuarios/cambiar-empresa`, data)
    }

    async passwordRecover(data) {
        return await ApiHelper.post(`/api/usuarios/password-recover`, data)
    }

    async validatePasswordRecoverToken(token) {
        return await ApiHelper.get(`/api/usuarios/validate-password-recover-token/${token}`)
    }

    async confirmPasswordRecover(data) {
        return await ApiHelper.post(`/api/usuarios/confirm-password-recover`, data)
    }

    async updatePassword(data) {
        return await ApiHelper.put(`/api/usuarios/update-password`, data)
    }

    async getUsers() {
        return await ApiHelper.get(`/api/usuarios/list`)
    }

    async getUserById(userId) {
        return await ApiHelper.get(`/api/usuarios/get/${userId}`)
    }

    async createUser(user) {
        return await ApiHelper.post(`/api/usuarios/create`, user)
    }

    async updateUser(id, user) {
        return await ApiHelper.put(`/api/usuarios/update/${id}`, user)
    }

    async deleteUser(id) {
        return await ApiHelper.delete(`/api/usuarios/delete/${id}`)
    }

    async uploadUserAvatar(data) {
        return await ApiHelper.postWithFiles(`/api/usuarios/upload-avatar`, data)
    }

    async uploadBrand(data) { 
        return await ApiHelper.postWithFiles(`/api/empresas/subir-logotipo`, data)
    }

    async updateAccount(data) { 
        return await ApiHelper.put(`/api/empresas/update`, data)
    }

    async getMisEmpresas() {
        return await ApiHelper.get(`/api/empresas/mis-empresas`)
    }

    async getContadores(empresaId) {
        return await ApiHelper.get(`/api/empresas/${empresaId}/contadores`)
    }

    async invitarContador(empresaId, data) {
        return await ApiHelper.post(`/api/empresas/${empresaId}/invitar-contador`, data)
    }

    async verificarInvitacion(token) {
        return await ApiHelper.get(`/api/empresas/verificar-invitacion/${token}`)
    }

    async aceptarInvitacionEmpresa(data) {
        return await ApiHelper.post(`/api/empresas/aceptar-invitacion`, data)
    }

    async reenviarInvitacion(empresaId, userId) {
        return await ApiHelper.post(`/api/empresas/${empresaId}/reenviar-invitacion/${userId}`)
    }

    async removerContador(empresaId, userId) {
        return await ApiHelper.delete(`/api/empresas/${empresaId}/remover-contador/${userId}`)
    }

    // Lugares
    async getLugares() {
        return await ApiHelper.get(`/api/lugares-expedicion/list`)
    }

    async getLugar(id) {
        return await ApiHelper.get(`/api/lugares-expedicion/get/${id}`)
    }

    async createLugares(data) {
        return await ApiHelper.post(`/api/lugares-expedicion/create`, data)
    }

    async updateLugares(data) {
        return await ApiHelper.put(`/api/lugares-expedicion/update`, data)
    }

    async deleteLugar(id) {
        return await ApiHelper.delete(`/api/lugares-expedicion/delete/${id}`)
    }

    // Facturas
    async getFacturas(fromDate, toDate) {
        const from = encodeURIComponent(toApiDate(fromDate))
        const to = encodeURIComponent(toApiDate(toDate))
        return await ApiHelper.get(`/api/facturas/list/${from}/${to}`)
    }

    async getInvoiceDraft(id) {
        return ApiHelper.get(`/api/facturas/get-draft/${id}`)
    }

    async deleteInvoiceDraft(id) {
        return ApiHelper.delete(`/api/facturas/delete-draft/${id}`)
    }

    async downloadInvoice(uuid, type) {
        return ApiHelper.download(`/api/facturas/get-${type}/${uuid}`, uuid, type)
    }

    async downloadAcuseCancelacion(uuid, type) {
        const endpoint = type === 'pdf'
            ? `/api/facturas/get-acuse-cancelacion-pdf/${uuid}`
            : `/api/facturas/get-acuse-cancelacion/${uuid}`

        return ApiHelper.download(endpoint, `${uuid}-acuse-cancelacion`, type)
    }

    async cancelarFactura(params) {
        return await ApiHelper.post(`/api/facturas/cancelar/`, params)
    }

    async verificarFacturaSat(id) {
        return await ApiHelper.post(`/api/facturas/${id}/verificar-sat`, {})
    }

    async timbrarFactura(data) {
        return await ApiHelper.post(`/api/facturas/timbrar`, data)
    }

    async getInvoicePreview(data) {
        return await ApiHelper.getPDFBlob(`/api/facturas/generate-preview`, data)
    }

    async saveAsDraft(data) {
        return await ApiHelper.post(`/api/facturas/save-draft`, data)
    }

    async duplicarFactura(id, params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        return await ApiHelper.post(`/api/facturas/${id}/duplicar${qs ? '?' + qs : ''}`)
    }

    async cerrarSaldoPrefill(id) {
        return await ApiHelper.post(`/api/facturas/${id}/cerrar-saldo-prefill`)
    }

    async registrarPagoPue(id, data) {
        return await ApiHelper.post(`/api/facturas/${id}/pagos/pue`, data)
    }

    async registrarPagoExterno(id, data) {
        return await ApiHelper.post(`/api/facturas/${id}/pagos/externo`, data)
    }

    async revertirPagoManual(invoiceId, paymentId, motivo) {
        return await ApiHelper.post(`/api/facturas/${invoiceId}/pagos/${paymentId}/revertir`, { motivo })
    }

    async getFacturaByUuid(uuid) {
        return await ApiHelper.get(`/api/facturas/by-uuid/${uuid}`)
    }

    async getFactura(id) {
        return await ApiHelper.get(`/api/facturas/${id}`)
    }

    async getFacturasTimbradas(params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        return await ApiHelper.get(`/api/facturas/timbradas${qs ? '?' + qs : ''}`)
    }

    async programarFactura(id, data) {
        return await ApiHelper.post(`/api/facturas/${id}/programar`, data)
    }

    async getFacturasProgramadas() {
        return await ApiHelper.get(`/api/facturas/programadas`)
    }

    async getFacturasPorCobrar() {
        return await ApiHelper.get(`/api/facturas/porcobrar`)
    }

    async desprogramarFactura(id) {
        return await ApiHelper.delete(`/api/facturas/${id}/desprogramar`)
    }

    async reintentarFactura(id) {
        return await ApiHelper.post(`/api/facturas/${id}/reintentar`)
    }

    async obtenerGraficaAnual(data) {
        return await ApiHelper.post(`/api/graficas/obtenerAnual`, data)
    }

    async getTotalesFacturados(data) {
        return await ApiHelper.get(`/api/graficas/totales-facturados`)
    }

    async getDashboardResumen() {
        return await ApiHelper.get(`/api/dashboard/resumen`)
    }

    async getDashboardAlertas() {
        return await ApiHelper.get(`/api/dashboard/alertas`)
    }

    async getDashboardCobranza() {
        return await ApiHelper.get(`/api/dashboard/cobranza`)
    }

    async getDashboardClientesTop() {
        return await ApiHelper.get(`/api/dashboard/clientes-top`)
    }

    async getDashboardSalud() {
        return await ApiHelper.get(`/api/dashboard/salud`)
    }

    // CUSTOMERS
    async getCustomerById(id) {
        return await ApiHelper.get(`/api/clientes/get/${id}`)
    }

    async getCustomers() {
        return await ApiHelper.get(`/api/clientes/list`)
    }

    async deleteCustomer(id) {
        return await ApiHelper.delete(`/api/clientes/delete/${id}`)
    }

    async createCustomer(data) {
        return await ApiHelper.post(`/api/clientes/create`, data)
    }

    async updateCustomer(id, data) {
        return await ApiHelper.put(`/api/clientes/update/${id}`, data)
    }

    // SERVICES
    async getServices() {
        return await ApiHelper.get(`/api/servicios/list`)
    }

    async getService(id) {
        return await ApiHelper.get(`/api/servicios/get/${id}`)
    }

    async createService(data) {
        return await ApiHelper.post(`/api/servicios/create`, data)
    }

    async updateService(id, data) {
        return await ApiHelper.put(`/api/servicios/update/${id}`, data)
    }

    async deleteService(id) {
        return await ApiHelper.delete(`/api/servicios/delete/${id}`)
    }

    async getUnidades() {
        return await ApiHelper.get(`/api/unidades/list`)
    }

    async createUnidad(data) {
        return await ApiHelper.post(`/api/unidades/create`, data)
    }

    async deleteUnidad(id) {
        return await ApiHelper.delete(`/api/unidades/delete/${id}`)
    }

    async getAduanas() {
        return await ApiHelper.get(`/api/aduanas/list`)
    }

    async searchForAduanaInSAT(keyword) {
        return await ApiHelper.get(`/api/aduanas/search/${keyword}`)
    }

    async createAduana(data) {
        return await ApiHelper.post(`/api/aduanas/create`, data)
    }

    async deleteAduana(id) {
        return await ApiHelper.delete(`/api/aduanas/delete/${id}`)
    }

    async getPatentes() {
        return await ApiHelper.get(`/api/patentes/list`)
    }

    async searchForPatenteInSAT(keyword) {
        return await ApiHelper.get(`/api/patentes/search/${keyword}`)
    }

    async createPatente(data) {
        return await ApiHelper.post(`/api/patentes/create`, data)
    }

    async deletePatente(id) {
        return await ApiHelper.delete(`/api/patentes/delete/${id}`)
    }

    async getClaves() {
        return await ApiHelper.get(`/api/cps/list`)
    }

    async searchForUnidadInSAT(keyword) { 
        return await ApiHelper.get(`/api/unidades/search/${keyword}`)
    }

    async createCPS(data) {
        return await ApiHelper.post(`/api/cps/create`, data)
    }

    async deleteCPS(id) {
        return await ApiHelper.delete(`/api/cps/delete/${id}`)
    }

    async searchForCPSInSAT(keyword) { 
        return await ApiHelper.get(`/api/cps/search/${keyword}`)
    }

    async getClavesSAT() {
        return await ApiHelper.get(`/api/cps/sat`)
    }

    async getCertificados() {
        return await ApiHelper.get(`/api/certificados/list`)
    }

    async getCertificado() {
        return await ApiHelper.get(`/api/certificados/get`)
    }

    async deleteCertificado(id) {
        return await ApiHelper.delete(`/api/certificados/delete/${id}`)
    }

    async createCertificado(data) {
        return await ApiHelper.post(`/api/certificados/create`, data)
    }

    async getPlans() {
        return await ApiHelper.get(`/api/planes/get`)
    }

    async createSubscription(paymentData) {
        return await ApiHelper.post(`/api/suscripciones/create`, paymentData)
    }

    async getSubscriptionStatus() {
        return await ApiHelper.get(`/api/pagos/subscription-status`)
    }

    async cambiarPlan(data) {
        return await ApiHelper.post(`/api/pagos/cambiar-plan`, data)
    }

    async comprarTimbres(data) {
        return await ApiHelper.post(`/api/pagos/comprar-timbres`, data)
    }

    async comprarTimbresConMetodoGuardado(data) {
        return await ApiHelper.post(`/api/pagos/comprar-timbres-guardado`, data)
    }

    async comprarTimbresConNuevaTarjeta(data) {
        return await ApiHelper.post(`/api/pagos/comprar-timbres-nueva-tarjeta`, data)
    }

    async comprarTimbresOxxoSpei(data) {
        return await ApiHelper.post(`/api/pagos/comprar-timbres-oxxo-spei`, data)
    }

    async getCreditPacks() {
        return await ApiHelper.get(`/api/pagos/credit-packs`)
    }

    async exchangeToken(token) {
        return await ApiHelper.get(`/api/auth/exchange?token=${encodeURIComponent(token)}`)
    }

    async getMetodoPago() {
        return await ApiHelper.get(`/api/pagos/metodo-pago`)
    }

    async getInvoicesFromStripe(paymentProviderCustomerId) {
        return await ApiHelper.post(`/api/suscripciones/get-invoices`, paymentProviderCustomerId)
    }

    async fetchDefaultPaymentMethod() {
        return await ApiHelper.get(`/api/suscripciones/get-default-payment-method`)
    }

    async getProfile() {
        return await ApiHelper.get(`/api/usuarios/get-profile`)
    }

    // Complementos de Pago
    async getFacturasPendientes(params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        return await ApiHelper.get(`/api/complementos/facturas-pendientes${qs ? '?' + qs : ''}`)
    }

    async getFacturasNoComplementables(params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        return await ApiHelper.get(`/api/complementos/facturas-no-complementables${qs ? '?' + qs : ''}`)
    }

    async timbrarComplemento(data) {
        return await ApiHelper.post(`/api/complementos`, data)
    }

    async importarComplemento(data) {
        return await ApiHelper.postWithFiles(`/api/complementos/importar`, data)
    }

    async verificarComplementoSat(id) {
        return await ApiHelper.post(`/api/complementos/${id}/verificar-sat`, {})
    }

    async getComplementos(params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        return await ApiHelper.get(`/api/complementos${qs ? '?' + qs : ''}`)
    }

    async cancelarComplemento(id, data) {
        return await ApiHelper.post(`/api/complementos/${id}/cancelar`, data)
    }

    async downloadComplementoPDF(id, uuid) {
        return ApiHelper.download(`/api/complementos/${id}/pdf`, uuid || id, 'pdf')
    }

    async downloadComplementoXML(id, uuid) {
        return ApiHelper.download(`/api/complementos/${id}/xml`, uuid || id, 'xml')
    }

    async getBitacora(params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        return await ApiHelper.get(`/api/bitacora${qs ? '?' + qs : ''}`)
    }

    async getMiRol() {
        return await ApiHelper.get(`/api/usuarios/mi-rol`)
    }

    async getFolios() {
        return await ApiHelper.get(`/api/folios`)
    }

    async updateFolio(tipoMovimiento, data) {
        return await ApiHelper.patch(`/api/folios/${tipoMovimiento}`, data)
    }

    async iniciarSatSync(data) {
        return await ApiHelper.postWithFiles(`/api/sat-sync/iniciar`, data)
    }

    async getSatSyncPreview() {
        return await ApiHelper.get(`/api/sat-sync/preview`)
    }

    async getSatSyncStatus(jobId) {
        return await ApiHelper.get(`/api/sat-sync/status/${jobId}`)
    }

    async getSatSyncHistorial() {
        return await ApiHelper.get(`/api/sat-sync/historial`)
    }

    async getEgresos(params = {}) {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        return await ApiHelper.get(`/api/egresos${qs ? '?' + qs : ''}`)
    }

    async createEgreso(data) {
        return await ApiHelper.postWithFiles(`/api/egresos`, data)
    }

    async updateEgreso(id, data) {
        if (data instanceof FormData) {
            return await ApiHelper.patchWithFiles(`/api/egresos/${id}`, data)
        }

        return await ApiHelper.patch(`/api/egresos/${id}`, data)
    }

    async deleteEgreso(id) {
        return await ApiHelper.delete(`/api/egresos/${id}`)
    }

    async getResumenEgresos() {
        return await ApiHelper.get(`/api/egresos/resumen`)
    }

    async getEgreso(id) {
        return await ApiHelper.get(`/api/egresos/${id}`)
    }

    async getEgresoByUuid(uuid) {
        return await ApiHelper.get(`/api/egresos/by-uuid/${encodeURIComponent(uuid)}`)
    }

    async descargarEgreso(id) {
        return await ApiHelper.postBlob(`/api/egresos/${id}/descargar`)
    }
}

export default new ApiService()
